import { formatPlayTime, type PeriodSummary, type PeriodComparison } from "../../hooks/useProgressReport";

export interface SummaryStatsProps {
  summary: PeriodSummary;
  comparison: PeriodComparison;
}

interface StatCardProps {
  icon: string;
  label: string;
  value: string | number;
  change?: number;
  colorClass: string;
}

function StatCard({ icon, label, value, change, colorClass }: StatCardProps) {
  const hasChange = change !== undefined && change !== 0;

  return (
    <div className="relative bg-cloud rounded-xl p-4 shadow-sm border border-neutral-100 hover:shadow-md transition-shadow print:shadow-none print:border-neutral-200">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span className="text-2xl" role="img" aria-hidden="true">
            {icon}
          </span>
          <div>
            <p className={`font-display font-bold text-2xl ${colorClass}`}>
              {value}
            </p>
            <p className="font-body text-sm text-neutral-500">{label}</p>
          </div>
        </div>

        {hasChange && (
          <div
            className={`flex items-center gap-0.5 px-2 py-1 rounded-lg text-xs font-body font-medium print:hidden ${
              change > 0
                ? "bg-meadow-100 text-meadow-700"
                : "bg-fairy-100 text-fairy-700"
            }`}
          >
            <span>{change > 0 ? "↑" : "↓"}</span>
            <span>{Math.abs(change)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Summary statistics grid showing key metrics for the period.
 * Features animated stat cards with comparison indicators.
 */
export function SummaryStats({ summary, comparison }: SummaryStatsProps) {
  const changes = comparison.changes;

  return (
    <div className="space-y-4">
      <h2 className="font-display font-bold text-display-sm text-neutral-800 flex items-center gap-2">
        <span className="text-xl">✨</span>
        Summary
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon="⏱️"
          label="Time Played"
          value={formatPlayTime(summary.totalTimePlayedSeconds)}
          change={changes ? Math.round(changes.timePlayedChange / 60) : undefined}
          colorClass="text-sky-600"
        />

        <StatCard
          icon="🎯"
          label="Challenges"
          value={summary.challengesCompleted}
          change={changes?.challengesChange}
          colorClass="text-magic-600"
        />

        <StatCard
          icon="⭐"
          label="Stars Earned"
          value={summary.starsEarned}
          change={changes?.starsChange}
          colorClass="text-sunshine-600"
        />

        <StatCard
          icon="🏅"
          label="Badges"
          value={summary.badgesEarned}
          colorClass="text-fairy-600"
        />
      </div>

      {/* Additional stats row */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-meadow-50 to-meadow-100/50 rounded-xl p-4 border border-meadow-200/50">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">📅</span>
            <p className="font-body text-sm font-medium text-meadow-700">Days Active</p>
          </div>
          <p className="font-display font-bold text-xl text-meadow-600">
            {summary.daysActive} day{summary.daysActive !== 1 ? "s" : ""}
          </p>
        </div>

        <div className="bg-gradient-to-br from-sky-50 to-sky-100/50 rounded-xl p-4 border border-sky-200/50">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">📊</span>
            <p className="font-body text-sm font-medium text-sky-700">Avg Session</p>
          </div>
          <p className="font-display font-bold text-xl text-sky-600">
            {summary.averageSessionMinutes} min
          </p>
        </div>
      </div>
    </div>
  );
}

export default SummaryStats;
